import { IconButton, Stack, Tooltip } from "@mui/material";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { oneBranchHasPermission } from "src/utils";
import { PurchasingPermissions } from "src/constants/permissions";
import EditIcon from "src/assets/svg-icons/aggregators/edit.svg";
import {
  useDeleteAccountMutation,
  useDeleteRecordMutation,
} from "src/api/tree/tree";
import AddEditRecord from "../add-edit-record-account";
import AddEditPayment from "src/pages/settings/tabs/payment-list/add-edit-payment";
import { useDeletePaymentMutation } from "src/api/settings/payments";
import { useDeleteCapitalMutation } from "src/api/settings/capital";
import EditCategory from "../edit-category";
import AddEditAccountPopup from "src/pages/operation-cost/tabs/accounts/add-new-account";
import AddEditContributorPopup from "src/pages/settings/tabs/capital/tabs/owners/add-edit-contributor";
import AddEditVendorPopup from "src/pages/operation-cost/tabs/vendors/add-edit-vendor";
import AddEditSuppliesPopup from "src/pages/purchasing/tabs/suppliers/add-edit-supplier";

interface IAction {
  row: any;
  refetch: Function;
}

const TableActions = (props: IAction) => {
  const { t } = useTranslation("general");
  const { row, refetch } = props;
  const { id, name } = row;
  const [openDelete, setOpenDelete] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [openEditCategory, setOpenEditCategory] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [editType, setEditType] = useState("");

  // APIS
  const { mutate, isPending, status, error } = useDeleteAccountMutation();
  const {
    mutate: mutateDeleteRecord,
    isPending: isPendingDeleteRecord,
    status: statusDeleteRecord,
    error: errorDeleteRecord,
  } = useDeleteRecordMutation();
  const {
    mutate: mutateDeletePayment,
    isPending: isPendingDeletePayment,
    status: statusDeletePayment,
    error: errorDeletePayment,
  } = useDeletePaymentMutation();
  const {
    mutate: mutateDeleteCapital,
    isPending: isPendingDeleteCapital,
    status: statusDeleteCapital,
    error: errorDeleteCapital,
  } = useDeleteCapitalMutation();

  //permissions
  const deletePermission = oneBranchHasPermission(
    PurchasingPermissions.deleteEvent,
  );

  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);

  const handleOpenEdit = () => {
    if (row.source_type === "payment_method") {
      setEditType("payment_method");
    } else if (
      row.node_type === "subcategory" ||
      row.node_type === "category"
    ) {
      setOpenEditCategory(true);
    } else if (row.source_type === "operation_cost_account") {
      setEditType("operation_cost_account");
    } else if (row.source_type === "capital") {
      setEditType("capital");
    } else if (row.source_type === "vendor") {
      setEditType("vendor");
    } else if (row.source_type === "supplier") {
      setEditType("supplier");
    } else {
      setOpenEditPopup(true);
    }
  };

  const handleCloseEditPopup = () => setOpenEditPopup(false);
  const handleCloseEditCategory = () => setOpenEditCategory(false);

  const handleCloseEdit = () => setEditType("");

  const handleDelete = () => {
    if (row.node_type === "account") {
      mutate(id);
    } else if (row.source_type === "payment_method") {
      mutateDeletePayment(row.source_id);
    } else if (row.source_type === "capital") {
      mutateDeleteCapital(row.source_id);
    } else {
      mutateDeleteRecord(id);
    }
  };

  useEffect(() => {
    if (
      status === "success" ||
      statusDeleteRecord === "success" ||
      statusDeletePayment === "success" ||
      statusDeleteCapital === "success"
    ) {
      refetch();
      handleCloseDelete();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("deletedSuccessfully"),
        },
      ]);
    } else if (
      status === "error" ||
      statusDeleteRecord === "error" ||
      statusDeletePayment === "error" ||
      statusDeleteCapital === "error"
    ) {
      const apiError =
        status === "error"
          ? error
          : statusDeleteRecord === "error"
            ? errorDeleteRecord
            : statusDeleteCapital === "error"
              ? errorDeleteCapital
              : errorDeletePayment;
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: apiError?.data.errors?.[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status, statusDeleteRecord, statusDeletePayment, statusDeleteCapital]);

  console.log({ row });

  return (
    <Stack direction={"row"} spacing={"12px"} justifyContent={"flex-start"}>
      {row.is_editable && (
        <Tooltip title={t("edit")}>
          <IconButton onClick={handleOpenEdit}>
            <img src={EditIcon} alt="" />
          </IconButton>
        </Tooltip>
      )}

      {row.is_deletable && (
        <Tooltip title={t("delete")}>
          <IconButton onClick={handleOpenDelete}>
            <img src={DeleteIcon} alt="" />
          </IconButton>
        </Tooltip>
      )}

      {/* confirm delete popup */}
      <ConfirmPopup
        open={openDelete}
        item={name}
        handleClose={handleCloseDelete}
        handleConfirm={handleDelete}
        isPending={
          isPending ||
          isPendingDeleteRecord ||
          isPendingDeletePayment ||
          isPendingDeleteCapital
        }
      />
      {/* edit popup */}

      {openEditPopup && (
        <AddEditRecord
          open={openEditPopup}
          handleClose={handleCloseEditPopup}
          dataToEdit={row}
          refetch={refetch}
        />
      )}

      {editType === "operation_cost_account" && (
        <AddEditAccountPopup
          open={editType === "operation_cost_account"}
          handleClose={handleCloseEdit}
          row={{
            id: row.source_id,
            name: row.name,
            start_balance: row.start_balance,
          }}
          refetch={refetch}
        />
      )}

      {openEditCategory && (
        <EditCategory
          open={openEditCategory}
          handleClose={handleCloseEditCategory}
          refetch={refetch}
          dataToEdit={row}
        />
      )}

      {editType === "capital" && (
        <AddEditContributorPopup
          open={editType === "capital"}
          handleClose={handleCloseEdit}
          refetch={refetch}
          capitalData={{
            id: row.source_id,
            name: row.system_name,
          }}
          editNameOnly
        />
      )}

      {editType === "vendor" && (
        <AddEditVendorPopup
          open={editType === "vendor"}
          handleClose={handleCloseEdit}
          row={{
            id: row.source_id,
            name: row.name,
            start_balance: row.start_balance,
          }}
          refetch={refetch}
        />
      )}

      {editType === "supplier" && (
        <AddEditSuppliesPopup
          open={editType === "supplier"}
          handleClose={handleCloseEdit}
          supplierToUpdate={{
            id: row.source_id,
            name: row.name,
            start_balance: row.start_balance,
          }}
          editNameAndBalance
        />
      )}

      {editType === "payment_method" && (
        <AddEditPayment
          open={editType === "payment_method"}
          handleClose={handleCloseEdit}
          refetch={refetch}
          paymentData={row}
          isCashOnly
        />
      )}
    </Stack>
  );
};

export default TableActions;
